import type { GenericError, RequestStatus } from '@skynet/data-ws-core';

export enum LoanOverviewCommands {
  LoanOverview = 'LoanOverview',
}

export enum LoanTypes {
  Unknown = -1,
  LO = 0, // loan option
  LR = 1, // loan retainer
  LP = 2, // loan with strict profit requirements
  SL = 3, // sub loan
  OTC = 4, // over-the-counter loan
}

export interface LoanTypeOption {
  label: string;
  value: LoanTypes;
}

/** Profit value plus a human-readable breakdown shown in tooltips. */
export interface OverviewProfit {
  value: number;
  description: string;
}

/** Loan-level aggregate profit: an overall total plus a per-token breakdown. */
export interface OverviewProfitOverview {
  total: OverviewProfit;
  perToken: Record<string, OverviewProfit>;
}

/**
 * A single token balance bucket (spot / on-chain / one side of a futures
 * position). `amount`/`usd` are the effective (virtual-asset adjusted) values;
 * the `*NotModified` fields are the raw values before adjustment.
 */
export interface OverviewBalanceEntry {
  amount: number;
  usd: number;
  amountNotModified: number;
  usdNotModified: number;
  averagePrice: number;
  tokens: string[];
  profit: OverviewProfit | null;
}

/** Futures balances are split into short and long legs per token. */
export interface OverviewFuturesEntry {
  shorts: OverviewBalanceEntry;
  longs: OverviewBalanceEntry;
}

export type OverviewBalanceMap = Record<string, OverviewBalanceEntry>;
export type OverviewFuturesMap = Record<string, OverviewFuturesEntry>;

export interface OverviewBalances {
  bases: OverviewBalanceMap;
  quotes: OverviewBalanceMap;
  otherTokens: OverviewBalanceMap;
}

export interface OverviewFuturesBalances {
  bases: OverviewFuturesMap;
  quotes: OverviewFuturesMap;
  otherTokens: OverviewFuturesMap;
}

/** The loan overview object delivered by the WS `LoanOverview` command. */
export interface JsonLoanOverview {
  id: number;
  name: string;
  baseTokens: string[];
  quoteTokens: string[];
  otherTokens: string[];
  spotBalances: OverviewBalances;
  onChainBalances: OverviewBalances;
  futuresBalances: OverviewFuturesBalances;
  profitOverview: OverviewProfitOverview;
}

export interface JsonLoanOverviewSuccess {
  command: LoanOverviewCommands.LoanOverview;
  loanID: number;
  overview: JsonLoanOverview;
}

export interface LoanOverviewState {
  overview: JsonLoanOverview | null;
  loanID: number | null;
  requestStatus: RequestStatus;
  error: GenericError | null;
}
